import * as React from 'react'
import { Check } from 'lucide-react'
import { useGraphStore } from '../../stores/useGraphStore'
import {
    readNarrationStartedAt,
    writeNarrationStartedAt,
} from '../../stores/onboardingStorage'
import { NARRATION_LINES } from './copy'

// ~7s for the five lines: long enough to actually read each one. Same value
// in the static twin (onboardingBootNarration.ejs) — keep in sync.
const LINE_INTERVAL_MS = 1400
// Never hold the overlay hostage if the graph-loaded signal doesn't arrive.
const FALLBACK_TIMEOUT_MS = 15000

/**
 * Full-viewport loading overlay that speaks the product's verbs while the
 * user's first graph is built: from the import submit (above the import
 * dialog), across the page reload, until the graph is laid out. Completes
 * at max(narration duration, graph loaded) and calls onDone exactly once
 * per mounted instance — unless `canComplete` is false (the pre-reload
 * instance on /apps: there is no graph to wait for, the navigation that
 * follows the import removes it).
 *
 * The line progression runs on a sessionStorage-persisted clock
 * (onboardingStorage) so it is ONE continuous sequence across every
 * mount along the way: import submit → reload → boot → ready tree.
 */
export default function NarratedBuild({ onDone, canComplete = true }) {
    const isGraphLoaded = useGraphStore(state => state.isGraphLoaded)
    const [startedAt] = React.useState(() => {
        const persistedStartedAt = readNarrationStartedAt()
        if (persistedStartedAt) return persistedStartedAt
        const now = Date.now()
        writeNarrationStartedAt(now)
        return now
    })
    // The graph-load fallback counts from this instance's mount, not from
    // narration start — a slow import could burn the whole window before
    // the graph page even begins to load.
    const [mountedAt] = React.useState(() => Date.now())
    const [now, setNow] = React.useState(() => Date.now())
    const doneRef = React.useRef(false)

    // ProjectNotes syncs the tw-dark class only after boot; mirror the legacy
    // theme attribute now so the boot overlay isn't white in dark mode.
    React.useEffect(() => {
        if (document.documentElement.hasAttribute('theme')) {
            document.documentElement.classList.add('tw-dark')
        }
    }, [])

    // The server-rendered graph page paints a static twin of this overlay
    // (views/components/entries/onboardingBootNarration.ejs) so the narration
    // isn't interrupted by the loading screen while the bundle hydrates;
    // this instance replaces it.
    React.useEffect(() => {
        document.getElementById('onboarding-boot-narration')?.remove()
    }, [])

    React.useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 200)
        return () => window.clearInterval(timer)
    }, [])

    const elapsed = now - startedAt
    const lineIndex = Math.min(
        Math.floor(elapsed / LINE_INTERVAL_MS),
        NARRATION_LINES.length - 1
    )
    const narrationFinished =
        elapsed >= LINE_INTERVAL_MS * (NARRATION_LINES.length + 0.5)
    const fallbackElapsed = now - mountedAt >= FALLBACK_TIMEOUT_MS

    React.useEffect(() => {
        if (!canComplete) return
        if (!narrationFinished) return
        if (!isGraphLoaded && !fallbackElapsed) return
        if (doneRef.current) return
        doneRef.current = true
        onDone()
    }, [canComplete, narrationFinished, isGraphLoaded, fallbackElapsed, onDone])

    return (
        <div className="project-notes-scope">
            {/* Background mirrors MUI background.default (theme.js) so the
                overlay matches the screens before and after it. */}
            <div
                role="status"
                aria-live="polite"
                aria-label="Building your graph"
                className="tw-fixed tw-inset-0 tw-z-[1320] tw-flex tw-items-center tw-justify-center tw-bg-[#f0ecec] dark:tw-bg-[#333333]"
            >
                {/* The site's brand font (legacy style.css body font family),
                    not the mono stack — the overlay should read as part of
                    the app. Inline style: tailwind arbitrary font-family
                    values don't take quoted multi-word stacks well. */}
                <ul
                    className="tw-m-0 tw-list-none tw-space-y-2.5 tw-p-0 tw-text-[14px]"
                    style={{
                        fontFamily:
                            "'IstokWeb-Regular', Arial, Helvetica, sans-serif",
                    }}
                >
                    {NARRATION_LINES.map((line, index) => {
                        const done = index < lineIndex
                        const current = index === lineIndex
                        // While the narration holds on its last line waiting
                        // for the graph, the whole line blinks so the wait
                        // doesn't read as frozen.
                        const holdingLastLine = current && narrationFinished
                        return (
                            <li
                                key={line}
                                className={[
                                    'tw-flex tw-items-center tw-gap-2.5 tw-transition-opacity tw-duration-300',
                                    done
                                        ? 'tw-opacity-90'
                                        : current
                                        ? 'tw-opacity-100'
                                        : 'tw-opacity-0',
                                    holdingLastLine
                                        ? 'tw-animate-narration-blink'
                                        : '',
                                ].join(' ')}
                            >
                                {done ? (
                                    <Check
                                        className="tw-h-3.5 tw-w-3.5 tw-text-[#15734d] dark:tw-text-[#33c686]"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <span
                                        className={[
                                            'tw-h-2 tw-w-2 tw-rounded-full tw-bg-[#0089e0] dark:tw-bg-[#00b7ff]',
                                            current && !holdingLastLine
                                                ? 'tw-animate-pulse'
                                                : '',
                                        ].join(' ')}
                                        aria-hidden="true"
                                    />
                                )}
                                <span
                                    className={
                                        done
                                            ? 'tw-text-[#15734d] dark:tw-text-[#33c686]'
                                            : 'tw-text-foreground'
                                    }
                                >
                                    {line}
                                </span>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </div>
    )
}
